import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { SelectButton } from 'primereact/selectbutton';
import { Calendar } from '../../components/calendar';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { saveAs } from 'file-saver';

import FormTributos from '../components/FormTributos';
import { validarRegrasTributarias, resumoErros } from '../regrasTributarias';
import { gerarPlanilha, lerPlanilha } from '../planilhaTributos';
import {
  pesquisarRecebimentos, reabrirRecebimento, conferirLoteRecebimentos, resumoNcm, listarClientesRecebimento, mensagemErro,
  exportarPlanilhaRecebimentos, importarPlanilhaRecebimentos,
} from '../tributosApi';
import {
  StatusTag, formatarCnpj, formatarNcm, dateToIso, normalizarTributos, limparClassTribRegularIgual,
} from '../util';

const soDigitos = v => String(v || '').replace(/\D/g, '');
/** Valor comum a todos os itens (ou '' quando diferem). */
const valorComum = (itens, campo) => {
  const valores = [...new Set(itens.map(i => soDigitos(i[campo])))];
  return valores.length === 1 ? valores[0] : '';
};

const OPCOES_STATUS = [
  { label: 'Pendentes', value: 'PENDENTE' },
  { label: 'Conferidos', value: 'CONFERIDO' },
  { label: 'Já atualizados', value: 'ATUALIZADO' },
  { label: 'Ignorados', value: 'IGNORADO' },
  { label: 'Todos', value: 'TODOS' },
];

const INTERVALO_MS = 30000;

export default function Recebimentos({ onAlteracao }) {
  const navigate = useNavigate();

  const [status, setStatus] = useState('PENDENTE');
  const [codigoEan, setCodigoEan] = useState('');
  const [descricao, setDescricao] = useState('');
  const [filtroNcm, setFiltroNcm] = useState('');
  const [filtroCest, setFiltroCest] = useState('');
  const [periodoConferencia, setPeriodoConferencia] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [periodo, setPeriodo] = useState(() => {
    const ini = new Date();
    ini.setDate(ini.getDate() - 7);
    return [ini, new Date()];
  });
  const [lista, setLista] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const idsConhecidos = useRef(null);

  // ---- Seleção múltipla / conferência em lote
  const [selecionados, setSelecionados] = useState([]);
  const [loteVisivel, setLoteVisivel] = useState(false);
  const [formLote, setFormLote] = useState(normalizarTributos(null));
  const [manterNcmCest, setManterNcmCest] = useState(true);
  const [observacaoLote, setObservacaoLote] = useState('');
  const [salvandoLote, setSalvandoLote] = useState(false);
  const [errosLote, setErrosLote] = useState([]);

  // ---- Planilha Excel (exportação / importação)
  const [exportVisivel, setExportVisivel] = useState(false);
  const [exportCliente, setExportCliente] = useState(null);
  const [descricaoInicial, setDescricaoInicial] = useState('');
  const [descricaoFinal, setDescricaoFinal] = useState('');
  const [exportando, setExportando] = useState(false);
  const [importacao, setImportacao] = useState(null); // { arquivo, leitura, resultado }
  const [importando, setImportando] = useState(false);
  const inputPlanilha = useRef(null);
  const modoSelecao = status === 'PENDENTE';

  const ncmsDistintos = itens => [...new Set(itens.map(i => soDigitos(i.ncm)).filter(Boolean))];
  const ncmsSelecionados = ncmsDistintos(selecionados);
  const ncmDiferente = ncmsSelecionados.length > 1;
  const listaNcms = ncmsSelecionados.map(formatarNcm).join(', ');

  const alterarSelecao = novos => {
    const lista2 = novos || [];
    const antes = ncmsDistintos(selecionados).length;
    const depois = ncmsDistintos(lista2);
    if (depois.length > 1 && depois.length > antes) {
      toast.warn(`Atenção: produtos selecionados com NCM diferente (${depois.map(formatarNcm).join(', ')}).`, { toastId: 'ncm-diferente' });
    }
    setSelecionados(lista2);
  };

  const carregar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCarregando(true);
    try {
      const params = {
        status,
        codigoEan: codigoEan || undefined,
        descricao: descricao || undefined,
        cnpjEmpresa: cliente || undefined,
        dataInicial: periodo && periodo[0] ? dateToIso(periodo[0]) : undefined,
        dataFinal: periodo && (periodo[1] || periodo[0]) ? dateToIso(periodo[1] || periodo[0]) : undefined,
        ncm: soDigitos(filtroNcm) || undefined,
        cest: soDigitos(filtroCest) || undefined,
      };
      // Data de conferência (não se aplica à fila de pendentes); quando informada, substitui o período de recebimento
      if (status !== 'PENDENTE' && periodoConferencia && periodoConferencia[0]) {
        params.dataConferenciaInicial = dateToIso(periodoConferencia[0]);
        params.dataConferenciaFinal = dateToIso(periodoConferencia[1] || periodoConferencia[0]);
      }
      const dados = await pesquisarRecebimentos(params);

      if (status === 'PENDENTE') {
        const ids = new Set(dados.map(d => d.id));
        if (idsConhecidos.current) {
          const novos = dados.filter(d => !idsConhecidos.current.has(d.id));
          if (novos.length) toast.info(`${novos.length} novo(s) produto(s) recebido(s) para conferência.`);
        }
        idsConhecidos.current = ids;
      }

      setLista(dados);
    } catch (e) {
      if (!silencioso) toast.error(mensagemErro(e, 'Erro ao consultar recebimentos.'));
    } finally {
      if (!silencioso) setCarregando(false);
    }
  }, [status, codigoEan, descricao, filtroNcm, filtroCest, periodoConferencia, cliente, periodo]);

  useEffect(() => {
    idsConhecidos.current = null;
    setSelecionados([]);
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, cliente]);

  // Clientes para o filtro (atualiza junto com a lista)
  const carregarClientes = useCallback(() => {
    listarClientesRecebimento()
      .then(lista2 => setClientes((lista2 || []).map(c => ({
        value: c.cnpj,
        label: `${c.nome || 'Cliente sem cadastro'} · ${formatarCnpj(c.cnpj)}${c.pendentes ? ` · ${c.pendentes} pendente(s)` : ''}`,
      }))))
      .catch(() => setClientes([]));
  }, []);

  useEffect(() => { carregarClientes(); }, [carregarClientes]);

  useEffect(() => {
    if (status !== 'PENDENTE') return undefined;
    const timer = setInterval(() => carregar(true), INTERVALO_MS);
    return () => clearInterval(timer);
  }, [status, carregar]);

  const pesquisar = e => {
    e.preventDefault();
    carregar();
  };

  const reabrir = async r => {
    try {
      await reabrirRecebimento(r.id);
      toast.success('Recebimento reaberto para conferência.');
      onAlteracao();
      carregar();
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };

  const abrirLote = async () => {
    if (!selecionados.length) return;
    const ncm = valorComum(selecionados, 'ncm');
    const cest = valorComum(selecionados, 'cest');
    let base = normalizarTributos(null);
    // Mesmo NCM em todos: pré-preenche com a tributação mais frequente desse NCM na base
    if (ncm.length === 8) {
      try {
        const dados = await resumoNcm(ncm);
        if (dados && dados.modelo) base = limparClassTribRegularIgual(normalizarTributos(dados.modelo));
      } catch (e) {
        // sem sugestão: segue com o formulário vazio
      }
    }
    setFormLote({
      ...base, id: null, codigoEan: '', descricao: '', cnpjCliente: '', registroProduto: null, ncm, cest: cest || base.cest || '',
    });
    setManterNcmCest(true);
    setObservacaoLote('');
    setErrosLote([]);
    setLoteVisivel(true);
  };

  const aplicarLote = async () => {
    let { erros } = validarRegrasTributarias(formLote);
    if (manterNcmCest) {
      erros = erros.filter(e => e.campo !== 'cest');
    } else if (soDigitos(formLote.ncm).length !== 8) {
      toast.warn('Informe o NCM (8 dígitos) ou marque para manter o NCM/CEST de cada produto.');
      return;
    }
    if (erros.length) {
      toast.error(`Corrija a tributação antes de conferir: ${resumoErros(erros)}`);
      return;
    }
    setSalvandoLote(true);
    try {
      const r = await conferirLoteRecebimentos({
        ids: selecionados.map(s => s.id),
        tributos: formLote,
        manterNcmCest,
        observacao: observacaoLote || undefined,
      });
      const ok = (r.conferidos || 0) + (r.jaBaixados || 0);
      if (ok) toast.success(`${ok} recebimento(s) conferido(s).`);
      onAlteracao();
      await carregar();
      if (r.erros && r.erros.length) {
        // Mantém selecionados somente os que falharam, para corrigir e aplicar de novo
        const idsErro = new Set(r.erros.map(e => e.id));
        setSelecionados(atual => atual.filter(s => idsErro.has(s.id)));
        setErrosLote(r.erros);
        toast.warn(`${r.erros.length} produto(s) não foram conferidos. Veja os motivos na janela.`);
      } else {
        setSelecionados([]);
        setLoteVisivel(false);
      }
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao conferir os selecionados.'));
    } finally {
      setSalvandoLote(false);
    }
  };

  const colunaProduto = r => (
    <div className="tb-cell-prod">
      <strong>{r.descricao}</strong>
      <span className="tb-mono">{r.codigoEan || `Sem EAN · Registro ${r.registroProduto || '—'}`}</span>
    </div>
  );

  const colunaNota = r => (
    <div className="tb-cell-sub">
      <span>{r.numeroNotaFiscal ? `NF ${r.numeroNotaFiscal}` : '—'}</span>
      <small>{r.nomeFornecedor || formatarCnpj(r.cnpjFornecedor)}</small>
    </div>
  );

  const colunaFiscalNota = r => (
    <div className="tb-cell-sub tb-mono">
      <span>{r.ncm ? formatarNcm(r.ncm) : '—'}</span>
      <small>{[r.cest && `CEST ${r.cest}`, r.cfop && `CFOP ${r.cfop}`, r.cst && `CST ${r.cst}`].filter(Boolean).join(' · ')}</small>
    </div>
  );

  const colunaOrigem = r => (
    <div className="tb-cell-sub">
      <span>{r.nomeEmpresa || '—'}</span>
      <small>{formatarCnpj(r.cnpjEmpresa)}</small>
    </div>
  );

  const colunaData = r => (
    <div className="tb-cell-sub">
      <span>{r.dataHoraUltimoEnvio || r.dataHoraRecebimento}</span>
      <small>{r.quantidadeEnvios > 1 ? `${r.quantidadeEnvios} envios` : ''}</small>
    </div>
  );

  const colunaAcoes = r => (
    <div className="tb-row-actions">
      {r.status === 'PENDENTE' ? (
        <button type="button" className="tb-btn tb-btn-primary tb-btn-sm" onClick={() => navigate(`/tributos/conferencia/${r.id}`)}>
          <i className="pi pi-pencil" /> Conferir
        </button>
      ) : (
        <>
          <button type="button" className="tb-btn tb-btn-ghost tb-btn-sm" onClick={() => navigate(`/tributos/conferencia/${r.id}`)}>
            <i className="pi pi-eye" /> Ver
          </button>
          {r.status !== 'ATUALIZADO' ? (
            <button type="button" className="tb-btn tb-btn-ghost tb-btn-sm" title="Reabrir para nova conferência" onClick={() => reabrir(r)}>
              <i className="pi pi-replay" />
            </button>
          ) : null}
        </>
      )}
    </div>
  );

  // ---------------------------------------------------------------- Planilha Excel
  const abrirExportacao = () => {
    setExportCliente(cliente);
    setExportVisivel(true);
  };

  const exportar = async () => {
    const ini = descricaoInicial.trim();
    const fim = descricaoFinal.trim();
    if (ini && fim && ini.localeCompare(fim, 'pt-BR', { sensitivity: 'base' }) > 0) {
      toast.warn('A descrição inicial deve ser menor ou igual à descrição final.');
      return;
    }
    setExportando(true);
    try {
      const linhas = await exportarPlanilhaRecebimentos({
        cnpjEmpresa: exportCliente || undefined,
        descricaoInicial: ini || undefined,
        descricaoFinal: fim || undefined,
      });
      if (!linhas.length) {
        toast.info('Nenhum produto pendente de conferência com esses filtros.');
        return;
      }
      const buffer = await gerarPlanilha(linhas);
      const agora = new Date();
      const p = n => String(n).padStart(2, '0');
      const nome = `eloTributos_conferencia_${agora.getFullYear()}${p(agora.getMonth() + 1)}${p(agora.getDate())}_${p(agora.getHours())}${p(agora.getMinutes())}.xlsx`;
      saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), nome);
      toast.success(`Planilha gerada com ${linhas.length} produto(s).`);
      setExportVisivel(false);
    } catch (e) {
      toast.error(mensagemErro(e, 'Erro ao exportar a planilha.'));
    } finally {
      setExportando(false);
    }
  };

  const selecionarPlanilha = async e => {
    const arquivo = e.target.files && e.target.files[0];
    e.target.value = ''; // permite selecionar o mesmo arquivo novamente
    if (!arquivo) return;
    setImportando(true);
    try {
      const leitura = await lerPlanilha(await arquivo.arrayBuffer());
      if (leitura.modo !== 'recebimento') {
        toast.error('Esta é a planilha da Base de tributos. Importe-a na tela Base de tributos.');
        return;
      }
      if (!leitura.marcadas) {
        toast.warn(`Nenhuma das ${leitura.total} linha(s) da planilha está marcada como "Conferido".`);
        return;
      }
      setImportacao({ arquivo: arquivo.name, leitura, resultado: null });
    } catch (err) {
      toast.error(err && err.message ? err.message : 'Não foi possível ler a planilha.');
    } finally {
      setImportando(false);
    }
  };

  const confirmarImportacao = async () => {
    if (!importacao) return;
    setImportando(true);
    try {
      const resultado = await importarPlanilhaRecebimentos(importacao.leitura.itens);
      setImportacao(atual => ({ ...atual, resultado }));
      if (resultado.conferidos) toast.success(`${resultado.conferidos} produto(s) conferido(s) pela planilha.`);
      if (resultado.erros && resultado.erros.length) toast.warn(`${resultado.erros.length} linha(s) não foram importadas.`);
      onAlteracao();
      setSelecionados([]);
      carregar();
      carregarClientes();
    } catch (err) {
      toast.error(mensagemErro(err, 'Erro ao importar a planilha.'));
    } finally {
      setImportando(false);
    }
  };

  return (
    <div className="tb-page">
      <div className="tb-page-head">
        <div>
          <h1>Recebimentos</h1>
          <p>Produtos enviados pela Retaguarda na entrada de notas fiscais.</p>
        </div>
        <div className="tb-head-actions">
          <button type="button" className="tb-btn tb-btn-ghost" onClick={abrirExportacao}>
            <i className="pi pi-file-excel" /> Exportar planilha
          </button>
          <button type="button" className="tb-btn tb-btn-ghost" onClick={() => inputPlanilha.current && inputPlanilha.current.click()} disabled={importando}>
            <i className={importando ? 'pi pi-spin pi-spinner' : 'pi pi-upload'} /> Importar planilha
          </button>
          <input
            ref={inputPlanilha}
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            style={{ display: 'none' }}
            onChange={selecionarPlanilha}
          />
          <button type="button" className="tb-btn tb-btn-ghost" onClick={() => { carregar(); carregarClientes(); }}>
            <i className={`pi pi-refresh ${carregando ? 'pi-spin' : ''}`} /> Atualizar
          </button>
        </div>
      </div>

      <section className="tb-card">
        <form className="tb-filters" onSubmit={pesquisar}>
          <SelectButton value={status} options={OPCOES_STATUS} onChange={e => e.value && setStatus(e.value)} className="tb-segmented" />
          <div className="tb-filters-row">
            <span className="p-input-icon-left">
              <i className="pi pi-qrcode" />
              <InputText value={codigoEan} onChange={e => setCodigoEan(e.target.value)} placeholder="EAN" />
            </span>
            <Dropdown
              value={cliente}
              options={clientes}
              onChange={e => setCliente(e.value || null)}
              placeholder="Todos os clientes"
              filter
              filterBy="label"
              showClear
              emptyMessage="Nenhum cliente"
              emptyFilterMessage="Nenhum cliente encontrado"
              className="tb-dropdown tb-filtro-cliente"
              panelClassName="tb-dropdown-panel"
            />
            <span className="p-input-icon-left tb-grow">
              <i className="pi pi-search" />
              <InputText value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Descrição" />
            </span>
            <InputText className="tb-filtro-curto" value={filtroNcm} onChange={e => setFiltroNcm(e.target.value)} placeholder="NCM" maxLength={10} />
            <InputText className="tb-filtro-curto" value={filtroCest} onChange={e => setFiltroCest(e.target.value)} placeholder="CEST" maxLength={9} />
            {status !== 'PENDENTE' ? (
              <Calendar
                value={periodo}
                onChange={e => setPeriodo(e.value)}
                selectionMode="range"
                dateFormat="dd/mm/yy"
                readOnlyInput
                showIcon
                placeholder="Recebido em"
                tooltip="Período de recebimento"
                className="tb-filtro-data"
                disabled={!!(periodoConferencia && periodoConferencia[0])}
              />
            ) : null}
            {status !== 'PENDENTE' ? (
              <Calendar
                value={periodoConferencia}
                onChange={e => setPeriodoConferencia(e.value)}
                selectionMode="range"
                dateFormat="dd/mm/yy"
                readOnlyInput
                showIcon
                showButtonBar
                placeholder="Conferido em"
                tooltip="Data de conferência (quando informada, substitui o período de recebimento)"
                className="tb-filtro-data"
              />
            ) : null}
            <button type="submit" className="tb-btn tb-btn-primary"><i className="pi pi-filter" /> Filtrar</button>
          </div>
        </form>

        {modoSelecao && selecionados.length ? (
          <div className="tb-lote-barra">
            <span>
              <strong>{selecionados.length}</strong> produto(s) selecionado(s)
              {ncmDiferente ? (
                <span className="tb-tag tb-tag-warn tb-lote-alerta" title={listaNcms}>
                  <i className="pi pi-exclamation-triangle" /> {ncmsSelecionados.length} NCMs diferentes
                </span>
              ) : null}
            </span>
            <div>
              <button type="button" className="tb-btn tb-btn-ghost tb-btn-sm" onClick={() => setSelecionados([])}>
                <i className="pi pi-times" /> Limpar seleção
              </button>
              <button type="button" className="tb-btn tb-btn-primary tb-btn-sm" onClick={abrirLote}>
                <i className="pi pi-check-square" /> Conferir selecionados
              </button>
            </div>
          </div>
        ) : null}

        <DataTable
          value={lista}
          selectionMode={modoSelecao ? 'checkbox' : null}
          selection={modoSelecao ? selecionados : null}
          onSelectionChange={e => alterarSelecao(e.value)}
          showSelectAll={false}
          sortField="descricao"
          sortOrder={1}
          loading={carregando}
          paginator
          rows={100}
          rowsPerPageOptions={[100, 200, 500]}
          dataKey="id"
          responsiveLayout="stack"
          breakpoint="900px"
          emptyMessage={status === 'PENDENTE' ? 'Nenhum produto aguardando conferência.' : 'Nenhum recebimento encontrado.'}
          className="tb-table"
          onRowDoubleClick={e => navigate(`/tributos/conferencia/${e.data.id}`)}
        >
          {modoSelecao ? <Column selectionMode="multiple" headerStyle={{ width: '3rem' }} style={{ width: '3rem' }} /> : null}
          <Column header="Produto" field="descricao" sortable body={colunaProduto} />
          <Column header="Dados da NF" body={colunaFiscalNota} style={{ width: '13rem' }} />
          <Column header="Nota / Fornecedor" body={colunaNota} style={{ width: '14rem' }} />
          <Column header="Cliente" body={colunaOrigem} style={{ width: '13rem' }} />
          <Column header="Recebido em" body={colunaData} style={{ width: '11rem' }} />
          <Column header="Situação" body={r => <StatusTag status={r.status} />} style={{ width: '9rem' }} />
          <Column body={colunaAcoes} style={{ width: '9rem' }} />
        </DataTable>
      </section>

      <Dialog
        className="tb-dialog"
        header={`Conferir ${selecionados.length} produto(s) selecionado(s)`}
        visible={loteVisivel}
        onHide={() => !salvandoLote && setLoteVisivel(false)}
        style={{ width: '70rem', maxWidth: '96vw' }}
        maximizable
        footer={(
          <div className="tb-lote-footer">
            <span className="tb-lote-opcao">
              <Checkbox inputId="manterNcmCest" checked={manterNcmCest} onChange={e => setManterNcmCest(e.checked)} />
              <label htmlFor="manterNcmCest">Manter o NCM e o CEST de cada produto</label>
            </span>
            <div>
              <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setLoteVisivel(false)} disabled={salvandoLote}>Cancelar</button>
              <button type="button" className="tb-btn tb-btn-primary" onClick={aplicarLote} disabled={salvandoLote || !selecionados.length}>
                {salvandoLote ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-check" />} Conferir {selecionados.length} produto(s)
              </button>
            </div>
          </div>
        )}
      >
        {errosLote.length ? (
          <section className="tb-regras tb-regras-erro">
            <header><i className="pi pi-times-circle" /> {errosLote.length} produto(s) não conferido(s) — continuam selecionados</header>
            <ul>
              {errosLote.map(e => <li key={e.id} className="tb-regra-erro"><strong>{e.descricao}</strong>: {e.mensagem}</li>)}
            </ul>
          </section>
        ) : null}
        {ncmDiferente ? (
          <section className="tb-regras tb-regras-aviso">
            <header><i className="pi pi-exclamation-triangle" /> Produtos com NCM diferente</header>
            <ul>
              <li className="tb-regra-aviso">
                Os produtos selecionados têm {ncmsSelecionados.length} NCMs: {listaNcms}. Confira se a mesma tributação vale para todos.
              </li>
              {!manterNcmCest ? (
                <li className="tb-regra-aviso">Com a opção desmarcada, todos passarão a ter o NCM/CEST informado abaixo.</li>
              ) : null}
            </ul>
          </section>
        ) : null}
        <p className="tb-obs">
          A tributação abaixo será gravada em todos os produtos selecionados, que serão marcados como conferidos.
          Cada produto mantém a sua descrição{manterNcmCest ? ', NCM e CEST' : ''}.
        </p>
        <FormTributos valor={formLote} onChange={setFormLote} ocultarIdentificacao ncmCestDoProduto={manterNcmCest} />
        <div className="tb-field" style={{ marginTop: 12 }}>
          <label htmlFor="obsLote">Observação (opcional)</label>
          <InputText id="obsLote" value={observacaoLote} onChange={e => setObservacaoLote(e.target.value)} maxLength={500} />
        </div>
      </Dialog>

      <Dialog
        className="tb-dialog"
        header="Exportar planilha de conferência"
        visible={exportVisivel}
        onHide={() => !exportando && setExportVisivel(false)}
        style={{ width: '34rem', maxWidth: '95vw' }}
        footer={(
          <div>
            <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setExportVisivel(false)} disabled={exportando}>Cancelar</button>
            <button type="button" className="tb-btn tb-btn-primary" onClick={exportar} disabled={exportando}>
              {exportando ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-file-excel" />} Exportar
            </button>
          </div>
        )}
      >
        <p className="tb-obs">
          Exporta os produtos <strong>pendentes de conferência</strong> com a tributação atual (ou sugerida pelo NCM).
          Marque a coluna <strong>Conferido</strong> nas linhas revisadas e importe a planilha.
        </p>
        <div className="tb-grid">
          <div className="tb-field tb-span-12">
            <label htmlFor="expCliente">Cliente</label>
            <Dropdown
              inputId="expCliente"
              value={exportCliente}
              options={clientes}
              onChange={e => setExportCliente(e.value || null)}
              placeholder="Todos os clientes"
              filter
              filterBy="label"
              showClear
              className="tb-dropdown"
              panelClassName="tb-dropdown-panel"
            />
          </div>
          <div className="tb-field tb-span-6">
            <label htmlFor="expDescIni">Descrição inicial</label>
            <InputText id="expDescIni" value={descricaoInicial} onChange={e => setDescricaoInicial(e.target.value)} placeholder="Ex.: A" />
          </div>
          <div className="tb-field tb-span-6">
            <label htmlFor="expDescFim">Descrição final</label>
            <InputText id="expDescFim" value={descricaoFinal} onChange={e => setDescricaoFinal(e.target.value)} placeholder="Ex.: C" />
          </div>
        </div>
        <small className="tb-hint" style={{ whiteSpace: 'normal' }}>
          Em branco = sem limite. A descrição final inclui tudo o que começa com o texto informado (ex.: de &quot;A&quot; até &quot;C&quot; inclui &quot;CAFÉ&quot;).
        </small>
      </Dialog>

      <Dialog
        className="tb-dialog"
        header="Importar planilha de conferência"
        visible={!!importacao}
        onHide={() => !importando && setImportacao(null)}
        style={{ width: '46rem', maxWidth: '96vw' }}
        footer={importacao && !importacao.resultado ? (
          <div>
            <button type="button" className="tb-btn tb-btn-ghost" onClick={() => setImportacao(null)} disabled={importando}>Cancelar</button>
            <button type="button" className="tb-btn tb-btn-primary" onClick={confirmarImportacao} disabled={importando}>
              {importando ? <i className="pi pi-spin pi-spinner" /> : <i className="pi pi-check" />} Conferir {importacao.leitura.marcadas} produto(s)
            </button>
          </div>
        ) : (
          <div>
            <button type="button" className="tb-btn tb-btn-primary" onClick={() => setImportacao(null)}>Fechar</button>
          </div>
        )}
      >
        {importacao ? (
          <>
            <p>
              Arquivo <strong>{importacao.arquivo}</strong>: {importacao.leitura.total} linha(s),{' '}
              <strong>{importacao.leitura.marcadas}</strong> marcada(s) como conferida(s).
            </p>
            {!importacao.resultado ? (
              <p className="tb-obs">
                Cada linha marcada será validada e, se estiver correta, gravada e marcada como conferida.
                As linhas com erro não são gravadas.
              </p>
            ) : (
              <p>
                <span className="tb-tag tb-tag-ok"><i className="pi pi-check" /> {importacao.resultado.conferidos} conferido(s)</span>{' '}
                {importacao.resultado.erros && importacao.resultado.erros.length ? (
                  <span className="tb-tag tb-tag-danger"><i className="pi pi-times" /> {importacao.resultado.erros.length} com erro</span>
                ) : null}
              </p>
            )}
            {importacao.leitura.avisos && importacao.leitura.avisos.length ? (
              <section className="tb-regras tb-regras-aviso">
                <header><i className="pi pi-exclamation-triangle" /> Avisos da leitura</header>
                <ul>{importacao.leitura.avisos.slice(0, 50).map(a => <li key={a} className="tb-regra-aviso">{a}</li>)}</ul>
              </section>
            ) : null}
            {importacao.resultado && importacao.resultado.erros && importacao.resultado.erros.length ? (
              <section className="tb-regras tb-regras-erro tb-import-erros">
                <header><i className="pi pi-times-circle" /> Linhas não importadas</header>
                <ul>
                  {importacao.resultado.erros.map(e => (
                    <li key={`${e.linha}-${e.id}`} className="tb-regra-erro">
                      <strong>Linha {e.linha}</strong>{e.descricao ? ` · ${e.descricao}` : ''}: {e.mensagem}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}
          </>
        ) : null}
      </Dialog>
    </div>
  );
}

Recebimentos.propTypes = {
  onAlteracao: PropTypes.func.isRequired,
};
